import { useState } from 'react';
import { Box, FormControlLabel, Grid2 as Grid, Link, Switch, Typography } from '@mui/material';
import { ConditionalLoader, Section, ValuationStatusTracker } from 'dashboard409a/pages/Operations/components';
import Avatar from 'dashboard409a/pages/Operations/components/Avatar/Avatar';
import formatCurrency from 'dashboard409a/utilities/formatCurrency';
import { DEFAULT_VISIBLE_FIELDS, getFinancialFields } from './ValuedInformation.constants';
import { ValuedInformationProps } from './ValuedInformation.props';
import { Container, Field, FinancialField, FinancialFieldsGrid, HeaderRow, Label, PersonnelRow, StatusBadge, StyledLinkSpan, ToggleContainer, UserSection, Value } from './ValuedInformation.styles';

const ValuedInformation = ({ 
  valuedInformation, 
  isLoading,
  statusOptions,
  currentStatusId,
  onStatusChange,
  isStatusLoading = false,
}: ValuedInformationProps) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const allFields = getFinancialFields(valuedInformation);

  // Filter fields based on toggle state
  const visibleFields = allFields.filter(field =>
    isExpanded || DEFAULT_VISIBLE_FIELDS.includes(field.key as typeof DEFAULT_VISIBLE_FIELDS[number])
  );

  return (
    <Section title="Valuation">
      <Container>
        <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
          {/* Left Column - Main Content */}
          <Grid size={{ md: 8, sm: 12 }}>
            <HeaderRow>
              <Field>
                <ConditionalLoader isLoading={isLoading} width={150}>
                  <Link href={valuedInformation?.valuationUrl} target="_blank" rel="noopener noreferrer">
                    <StyledLinkSpan>{valuedInformation?.valuationName || '-'}</StyledLinkSpan>
                  </Link>
                </ConditionalLoader>
              </Field>
              <ConditionalLoader isLoading={isLoading} width={150}>
                <StatusBadge>{valuedInformation?.bookingType || '-'}</StatusBadge>
              </ConditionalLoader>
            </HeaderRow>
            {valuedInformation?.valuationNotice && (
              <span>NOTICE: {valuedInformation.valuationNotice}</span>
            )}
            <Box mt={0.5} />
            <ToggleContainer>
              <FormControlLabel
                control={
                  <Switch
                    checked={isExpanded}
                    onChange={e => setIsExpanded(e.target.checked)}
                    size="small"
                  />
                }
                label={isExpanded ? 'Show Less' : 'Show More'}
              />
            </ToggleContainer>
            <FinancialFieldsGrid>
              {visibleFields.map(field => {
                const value = field.getValue();
                let displayValue: string = '-';
                if (field.isCurrency) {
                  if (value !== undefined && value !== null && value !== '-') {
                    displayValue = formatCurrency({
                      value: typeof value === 'number' ? value : Number(value) || 0,
                    });
                  }
                } else {
                  displayValue = (value as string) || '-';
                }
                return (
                  <FinancialField key={field.key}>
                    <Label>{field.label}</Label>
                    <ConditionalLoader isLoading={isLoading} width={150}>
                      <Value>{displayValue}</Value>
                    </ConditionalLoader>
                  </FinancialField>
                );
              })}
            </FinancialFieldsGrid>
            <PersonnelRow>
              <Field>
                <Label>Valuation Lead</Label>
                <UserSection>
                  <Avatar
                    src={valuedInformation?.valuationLeadImageSrc || ''}
                    name={valuedInformation?.valuationLeadName || '-'}
                    roleTitle="Valuation Lead"
                    isLoading={isLoading}
                  />
                </UserSection>
              </Field>
              <Field>
                <Label>Engagement Manager</Label>
                <UserSection>
                  <Avatar
                    src={valuedInformation?.valuationEngagementManagerImageSrc || ''}
                    name={valuedInformation?.valuationEngagementManager || '-'}
                    roleTitle={valuedInformation?.valuationEngagementManagerRole || '-'}
                    isLoading={isLoading}
                  />
                </UserSection>
              </Field>
            </PersonnelRow>
          </Grid>

          {/* Right Column - Status Tracker */}
          {statusOptions && statusOptions.length > 0 && (
            <Grid size={{ md: 4, sm: 12 }} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box>
                <Typography variant="body1" fontWeight={600} sx={{ fontSize: '0.875rem' }}>Status</Typography>
              </Box>
              <Box mt={1} sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <ValuationStatusTracker
                  statusOptions={statusOptions}
                  currentStatusId={currentStatusId}
                  onStatusChange={onStatusChange}
                  isLoading={isStatusLoading}
                  isExpanded={isExpanded}
                />
              </Box>
            </Grid>
          )}
        </Grid>
      </Container>
    </Section>
  );
};

export default ValuedInformation;
